import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { ContentContainer } from '../components/HeroSectionStyles';
import ParticlesBackground from '../components/ParticlesBackground';

// Page transition animations
const slideInFromRight = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideOutToLeft = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(-40px); }
`;

const slideOutToRight = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(40px); }
`;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  padding: 20px;
  position: relative;
  overflow: hidden;

  &.enter-right { animation: ${slideInFromRight} 280ms ease-out both; }
  &.enter-left { animation: ${slideInFromLeft} 280ms ease-out both; }
  &.exit-left { animation: ${slideOutToLeft} 240ms ease-in both; }
  &.exit-right { animation: ${slideOutToRight} 240ms ease-in both; }
`;

const Content = styled(ContentContainer)`
  max-width: 1100px;
  width: 92%;
  margin: 0 auto;
  padding: 48px 56px;
  border: 1px solid rgba(100, 182, 255, 0.25);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.18);
  box-shadow: 0 0 20px rgba(100, 182, 255, 0.08);
  backdrop-filter: blur(8px);
  text-align: left;
`;

const SectionTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.6rem;
  color: #64B6FF;
  margin: 1.2rem 0 1rem;
  text-align: left;
  letter-spacing: 2px;
  text-shadow: 0 0 12px rgba(100, 182, 255, 0.5);
`;

const SectionSubtitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0.2rem 0 1.2rem;
  text-align: left;
  letter-spacing: 1px;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  max-width: 920px;
`;

const Item = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 14px 0;
  font-size: 1.35rem;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.5px;

  &::before {
    content: attr(data-idx) '.';
    color: #64B6FF;
    font-weight: 700;
    text-shadow: 0 0 10px rgba(100, 182, 255, 0.6);
    margin-top: 0;
    display: inline-block;
    width: 24px;
    flex: 0 0 24px;
    text-align: right;
  }
`;

const Subline = styled.div`
  font-size: 1.1rem;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.92);
  margin: 6px 0 0 0;
`;

const BackButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid rgba(100, 182, 255, 0.4);
  background: rgba(10, 18, 28, 0.6);
  color: #64B6FF;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 0 12px rgba(100, 182, 255, 0.25);
  backdrop-filter: blur(4px);
  transition: transform 120ms ease, box-shadow 200ms ease, background 200ms ease;

  &:hover {
    transform: translateX(-2px);
    box-shadow: 0 0 16px rgba(100, 182, 255, 0.45);
    background: rgba(10, 18, 28, 0.75);
  }
`;

const NextButton = styled(BackButton)`
  left: auto;
  right: 20px;

  &:hover {
    transform: translateX(2px);
  }
`;

function ReuniaoOnboardingIntegracoesCRM() {
  const location = useLocation();
  const navigate = useNavigate();

  const [anim, setAnim] = useState(() => (location.state?.dir === 'back' ? 'enter-left' : 'enter-right'));
  const TOTAL_ITEMS = 2;
  const [visibleCount, setVisibleCount] = useState(1);

  const goBack = () => {
    setAnim('exit-right');
    setTimeout(() => {
      navigate(`/reuniao-onboarding/script-bianca${location.search}`, { state: { dir: 'back' } });
    }, 240);
  };

  const goNext = () => {
    setAnim('exit-left');
    setTimeout(() => {
      navigate(`/onboarding${location.search}`, { state: { dir: 'forward' } });
    }, 240);
  };

  const revealNext = () => {
    setVisibleCount((c) => {
      if (c < TOTAL_ITEMS) return c + 1;
      goNext();
      return c;
    });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goBack();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        revealNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [location.search]);

  return (
    <>
      <ParticlesBackground />
      <PageWrapper className={anim}>
        <BackButton onClick={goBack} aria-label="Voltar">‹</BackButton>
        <NextButton onClick={revealNext} aria-label="Avançar">›</NextButton>
        <Content>
          <SectionTitle>Integrações com CRM</SectionTitle>
          <SectionSubtitle>Definir o CRM atual e as integrações desejadas para a Bianca.</SectionSubtitle>

          <List>
            {visibleCount >= 1 && (
              <Item data-idx="1">
                <div>
                  <strong>Qual CRM vocês utilizam?</strong>
                  <Subline>
                    Ex.: RD Station, HubSpot, Exact Sales, Pipedrive, outro.
                  </Subline>
                </div>
              </Item>
            )}

            {visibleCount >= 2 && (
              <Item data-idx="2">
                <div>
                  <strong>Quais integrações vocês gostariam que a Bianca tivesse com esse CRM?</strong>
                  <Subline>
                    Exemplos: criar/atualizar leads, registrar atividades (ligações/mensagens), etapas do funil, tags, tarefas de follow-up, webhooks, dashboards.
                  </Subline>
                </div>
              </Item>
            )}
          </List>
        </Content>
      </PageWrapper>
    </>
  );
}

export default ReuniaoOnboardingIntegracoesCRM;
